import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, API, TOKEN_KEY, formatApiErrorDetail } from "@/api/client";
import { toast } from "sonner";
import { Trash2, Download, Send } from "lucide-react";

export default function AdminNewsletter() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/newsletter"); setSubs(data); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remover este subscritor?")) return;
    try { await api.delete(`/admin/newsletter/${id}`); toast.success("Removido"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const sendDigest = async () => {
    if (subs.length === 0) { toast.error("Sem subscritores."); return; }
    if (!window.confirm(`Enviar digest semanal para ${subs.length} subscritor${subs.length === 1 ? "" : "es"}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/send-digest");
      toast.success(`Enviado a ${data.sent}${data.failed ? ` · ${data.failed} falhas` : ""}.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erro ao enviar.");
    } finally {
      setSending(false);
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/admin/newsletter/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `newsletter-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro ao exportar");
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-newsletter">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl text-stone-900">Newsletter</h1>
            <p className="mt-2 text-stone-600">{subs.length} subscritor{subs.length === 1 ? "" : "es"}.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={sendDigest} disabled={sending} data-testid="newsletter-send-digest" className="btn-primary">
              <Send className="h-4 w-4" /> {sending ? "A enviar..." : "Enviar digest semanal"}
            </button>
            <button onClick={exportCsv} data-testid="newsletter-export" className="btn-secondary"><Download className="h-4 w-4" /> Exportar CSV</button>
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <strong>Nota:</strong> O envio automático usa a Resend. Configure <code className="bg-white/70 px-1 rounded">RESEND_API_KEY</code> em <code className="bg-white/70 px-1 rounded">backend/.env</code> e reinicie o servidor. Enquanto não estiver definida, pode exportar CSV e enviar por outra ferramenta.
        </div>

        <div className="mt-8 card-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Email</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Origem</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-stone-500">Data</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="p-6 text-stone-500">A carregar...</td></tr>
              ) : subs.length === 0 ? (
                <tr><td colSpan={4} className="p-10 text-center text-stone-500">Sem subscritores ainda.</td></tr>
              ) : subs.map((s) => (
                <tr key={s.id} data-testid={`newsletter-row-${s.id}`} className="border-b border-stone-100 hover:bg-stone-50/60">
                  <td className="px-4 py-3 text-stone-800">{s.email}</td>
                  <td className="px-4 py-3 text-stone-600 text-xs">{s.source}</td>
                  <td className="px-4 py-3 text-stone-500 text-xs">{s.created_at ? new Date(s.created_at).toLocaleString("pt-PT") : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(s.id)} className="btn-ghost text-red-700" data-testid={`newsletter-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
